import { observable, computed } from 'mobx';
import Axios, { CancelTokenSource } from 'axios';

import { PersonInfoModel, PersonModel, ApiClient, IGetPersonInfoParams } from '@app/Models/WebApiModels';
import { RangeReadyStatus } from './Range';
import { IPersonStore } from './IPersonStore';

import { PromiseCompletion, CompletionType } from '@app/Classes/PromiseCompletion';
import DateTimeService from '@app/Services/DateTimeService';
import { applyDecorators } from '@app/Helpers/Decorator';

type ChangeSubscriber = () => void;

type PersonModelDto = Omit<PersonModel, 'isActive'>;

export class SinglePersonStore implements IPersonStore {
    @observable private _person: PersonModelDto | null = null;
    @observable private _personInfo: PersonInfoModel | null = null;
    private _dataLoading: PromiseCompletion;
    private _apiClient: ApiClient = new ApiClient();
    private _requestId: string | null = null;
    public date: Date | null = null;

    private _cancellationSourceAdditional: CancelTokenSource;
    private _changeSubscriptions: ChangeSubscriber[] = [];

    constructor(person: PersonModelDto | null) {
        applyDecorators(this);
        this._person = person;
        this._dataLoading = new PromiseCompletion(CompletionType.Completed);
        this._dataLoading.subscribe(Promise.resolve());
    }

    public onChangeSource(callback: ChangeSubscriber) {
        this._changeSubscriptions.push(callback);
    }

    private _cancelAdditionalData() {
        this._cancellationSourceAdditional?.cancel();
        this._cancellationSourceAdditional = Axios.CancelToken.source();
    }

    public updatePerson(person: PersonModelDto | null) {
        this._person = person;
        this._cancelAdditionalData();
        this._changeSubscriptions.forEach((s) => s());
    }

    public updateInfo(info: PersonInfoModel | null) {
        this._personInfo = info;
        this.date = null;
    }

    public async reloadInfo(date: Date) {
        if (this.isEmpty) {
            throw new Error(`Can't load infor on ${DateTimeService.toUiTimestamp(date)} because person is not defined`);
        }

        this.date = date;
        const requestId = Math.random().toString();
        this._requestId = requestId;

        const params: IGetPersonInfoParams = {
            date: date
        };
        if (this._person) params.personIds = [this._person.id];

        const { data: personInfos } = await this._apiClient.personInfoGet(params, { completion: this._dataLoading, cancellationToken: this._cancellationSourceAdditional?.token });

        if (requestId === this._requestId) {
            this._personInfo = personInfos[0];
        }
    }

    public get isEmpty() {
        return !this._person;
    }

    public get dataLoading(): PromiseCompletion {
        return this._dataLoading;
    }

    @computed
    public get filteredPersons() {
        return this._person ? [this._person] : [];
    }

    public getDetails() {
        return this._personInfo;
    }

    public getPerson(personId: string) {
        return this._person && this._person.id === personId ? this._person : null;
    }

    public loadViewport() {}
    public loadPersonInfo() { }

    public getStatus() {
        return this._dataLoading.isPending ? RangeReadyStatus.Loading : RangeReadyStatus.Ready;
    }
}
